// src/components/Blog/BlogDetail.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkSlug from "remark-slug";
import rehypeRaw from "rehype-raw";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import { Helmet } from "react-helmet-async";

import blogData from "./blogs/blogData.json";
import BlogLayout from "./BlogLayout";
import LeftRail from "./LeftRail";

export default function BlogDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const articleRef = useRef(null);

  const blog = useMemo(() => blogData.find((b) => b.slug === slug), [slug]);

  // Make any path absolute against current origin
  const abs = (p) => new URL(p, window.location.origin).toString();

  // Load markdown from /public (e.g., /blogs/slug.md)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!blog?.contentUrl) {
        setLoading(false);
        setLoadErr("Article meta not found (missing contentUrl).");
        return;
      }
      try {
        setLoading(true);
        setLoadErr("");

        const url = abs(blog.contentUrl);
        const res = await fetch(url, { cache: "no-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);

        const text = await res.text();
        const stripped = text.replace(/^---[\s\S]*?---\s*/, ""); // strip YAML front-matter if present
        if (!cancelled) setContent(stripped);
      } catch (e) {
        console.error("Markdown load failed:", e);
        if (!cancelled) setLoadErr(`Failed to load article from ${blog?.contentUrl}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (blog) load();
    return () => { cancelled = true; };
  }, [blog]);

  // Honor #hash after markdown mounts (smooth scroll with offset)
  useEffect(() => {
    if (!content) return;
    const hash = decodeURIComponent(location.hash || "").replace(/^#/, "");
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (!el) return;
      const headerOffset = 112; // match your sticky header height
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [content, location.hash]);

  if (!blog) return null;

  // Meta (title/description)
  const metaTitle = blog.meta?.title || `${blog.title} | MyBOQ`;
  const metaDescription = blog.meta?.description || blog.excerpt || "Article from MyBOQ.";

  // Resolve URLs for OG/Twitter + sharing
  const pageUrlAbs = new URL(window.location.href).toString(); // current route
  const shareUrlAbs = abs(blog.shareUrl || pageUrlAbs);        // prefer /s/*.html if provided
  const ogImageAbs = abs(blog.ogImage || blog.thumbnail || "/images/blog/placeholder.jpg");

  return (
    <BlogLayout
      title={blog.title}
      breadcrumb={[
        { label: "Home", to: "/" },
        { label: "Blog", to: "/blog" },
        { label: blog.title },
      ]}
      meta={{
        author: blog.author,
        date: blog.date,
        reading_time: blog.reading_time || "7–9 min",
      }}
      leftRail={
        <LeftRail
          containerRef={articleRef}
          title={blog.title}
          markdown={content}
          shareUrl={new URL(blog.shareUrl || window.location.href, window.location.origin).toString()}
        />
      }
    >
      <Helmet>
        {/* Basic */}
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={shareUrlAbs} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrlAbs} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImageAbs} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImageAbs} />
      </Helmet>

      {/* Responsive hero: fixed aspect (16:9 on mobile, 1200x630 on md+) */}
      {blog.thumbnail && (
        <figure className="w-full rounded-2xl border overflow-hidden mb-8">
          <div className="aspect-[16/9] md:aspect-[1200/630]">
            <img
              src={blog.thumbnail}
              alt={blog.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
        </figure>
      )}

      {/* Article content */}
      {loading ? (
        <div className="space-y-3 animate-pulse">
          <div className="h-6 w-3/4 bg-gray-100 rounded" />
          <div className="h-6 w-1/2 bg-gray-100 rounded" />
          <div className="h-4 w-full bg-gray-100 rounded" />
          <div className="h-4 w-5/6 bg-gray-100 rounded" />
          <div className="h-4 w-2/3 bg-gray-100 rounded" />
        </div>
      ) : loadErr ? (
        <div className="rounded-xl border bg-red-50 text-red-800 p-4">{loadErr}</div>
      ) : (
        <div
          ref={articleRef}
          className="
            prose prose-zinc lg:prose-lg max-w-none
            prose-p:my-5 prose-p:leading-8
            prose-li:my-2
            prose-ol:my-5 prose-ul:my-5
            prose-blockquote:my-6
            prose-hr:my-10
            prose-table:my-8
            prose-h2:mt-12 prose-h2:mb-4
            prose-h3:mt-8 prose-h3:mb-3
            prose-img:w-full prose-img:h-auto
          "
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkSlug]}
            rehypePlugins={[
              rehypeRaw,
              [rehypeAutolinkHeadings, { behavior: "append", properties: { className: ["no-underline"] } }],
            ]}
            components={{
              table: (props) => (
                <table
                  className="min-w-[720px] w-full border-separate border-spacing-0 overflow-hidden rounded-xl"
                  {...props}
                />
              ),
              thead: (props) => <thead className="bg-gray-50" {...props} />,
              th: (props) => (
                <th
                  className="text-left text-sm font-semibold text-gray-700 border-b border-gray-200 px-4 py-3"
                  {...props}
                />
              ),
              td: (props) => (
                <td
                  className="text-sm text-gray-700 border-b border-gray-100 px-4 py-3 align-top"
                  {...props}
                />
              ),
              tr: (props) => <tr className="odd:bg-white even:bg-gray-50" {...props} />,
              a: ({ node, children, href = "", ...rest }) => {
                // a11y-safe even if MD accidentally renders an empty <a>
                const hasContent =
                  (Array.isArray(children) && children.length > 0) ||
                  (typeof children === "string" && children.trim().length > 0);
                const label =
                  rest["aria-label"] ||
                  (hasContent ? undefined : href.replace(/^https?:\/\//, ""));
                return (
                  <a
                    href={href}
                    {...rest}
                    className="text-orange-600 hover:text-orange-700 underline underline-offset-2"
                    aria-label={label}
                  >
                    {hasContent ? children : <span className="sr-only">{href}</span>}
                    {!hasContent && <span aria-hidden="true">🔗</span>}
                  </a>
                );
              },
              // Responsive inline images from Markdown
              img: ({ src, alt, ...props }) => (
                <img
                  src={src}
                  alt={alt || ""}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto rounded-xl border object-contain"
                  {...props}
                />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      )}

      {/* Bottom content CTA (not your global site footer) */}
      <div className="mt-12 rounded-2xl border bg-gradient-to-r from-gray-50 to-white p-6 md:p-8">
        <div className="md:flex md:items-center md:justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold">Get a bank-ready BOQ today</h3>
            <p className="text-gray-600 mt-1">
              Use MyBOQ to generate SSR-aligned estimates and stage-wise summaries in minutes.
            </p>
          </div>
          <Link
            to="/signup"
            className="mt-4 md:mt-0 inline-flex items-center rounded-full bg-orange-600 px-6 py-3 font-semibold text-white hover:bg-orange-700"
          >
            Try MyBOQ Demo →
          </Link>
        </div>
      </div>
    </BlogLayout>
  );
}
